import { create } from "zustand";
import _get from "lodash/get";
import { api } from "network/network";
import { API_HOST_WMS } from "config/api.config";
import { APIendpoints } from "utils/constants/APIendpoints";
import {
  STATUS_BEGIN,
  STATUS_DONE,
  STATUS_FAIL,
} from "utils/constants/others";
import { makeEncodedQueryParams } from "operations/operations";
import { PAYOUTS_TABS } from "./config";

const buildQuery = (params) => {
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    parts.push(`${key}=${encodeURIComponent(value)}`);
  });
  return parts.length ? `?${parts.join("&")}` : "";
};

// null when the field is missing, so the screen falls back to the rows loaded so far
const toHitCount = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const usePayoutsStore = create((set, get) => ({
  // Screen filters live here, not in component state, so they survive leaving the
  // screen (payment details, other pages) and coming back. dueRange stays null until
  // the user picks one, so the default window is computed fresh on each visit.
  payoutFilters: {
    activeTab: PAYOUTS_TABS.PAYMENTS,
    paymentsFilter: [],
    transactionsFilter: [],
    dueRange: null,
    searchTerm: "",
  },
  accruals: [],
  accrualsStatus: "",
  accrualsRequestId: 0,
  accrualsHasMore: true,
  // total matching rows, read from the first page only; null until that page arrives
  accrualsHitCount: null,
  // offset of the next page; a page can come back with fewer rows than page_size
  accrualsNextOffset: 0,
  transactions: [],
  transactionsStatus: "",
  transactionsRequestId: 0,
  transactionsHasMore: true,
  transactionsHitCount: null,
  // offset of the next page, stepped by page_size like accrualsNextOffset
  transactionsNextOffset: 0,
  accrualDetails: null,
  accrualDetailsStatus: "",
  accrualDetailsRequestId: 0,

  getVendorAccruals: async (params, { append = false } = {}) => {
    const requestId = get().accrualsRequestId + 1;
    set({
      accruals: append ? get().accruals : [],
      accrualsStatus: STATUS_BEGIN,
      accrualsRequestId: requestId,
      accrualsHasMore: append ? get().accrualsHasMore : true,
      ...(append ? {} : { accrualsHitCount: null }),
    });
    const url = `${APIendpoints.getVendorAccruals}${buildQuery(params)}`;
    try {
      const response = await api.get(API_HOST_WMS, url);
      if (get().accrualsRequestId !== requestId) return;
      // the backend sends null, not [], for an empty page (_get's default only covers undefined)
      const list =
        _get(response, ["data", "response", "accrual_details"]) || [];
      const pageSize = Number(params.page_size) || list.length;
      // A page can come back with fewer rows than page_size, so step the offset by
      // page_size instead of by the rows received.
      const nextOffset = (Number(params.page_start_offset) || 0) + pageSize;
      set({
        accruals: append ? [...get().accruals, ...list] : list,
        accrualsStatus: STATUS_DONE,
        accrualsNextOffset: nextOffset,
        // an empty page means the list is complete, no more requests
        accrualsHasMore: list.length > 0,
        // the total comes from the first page only
        ...(append
          ? {}
          : {
              accrualsHitCount: toHitCount(
                _get(response, ["data", "response", "accruals_hit_count"])
              ),
            }),
      });
    } catch (error) {
      if (get().accrualsRequestId !== requestId) return;
      set({
        accruals: append ? get().accruals : [],
        accrualsStatus: STATUS_FAIL,
      });
    }
  },

  getVendorTransactions: async (params, { append = false } = {}) => {
    const requestId = get().transactionsRequestId + 1;
    set({
      transactions: append ? get().transactions : [],
      transactionsStatus: STATUS_BEGIN,
      transactionsRequestId: requestId,
      transactionsHasMore: append ? get().transactionsHasMore : true,
      ...(append ? {} : { transactionsHitCount: null }),
    });
    const url = `${APIendpoints.getVendorTransactions}${buildQuery(params)}`;
    try {
      const response = await api.get(API_HOST_WMS, url);
      if (get().transactionsRequestId !== requestId) return;
      // the backend sends null, not [], for an empty page (_get's default only covers undefined)
      const list =
        _get(response, ["data", "response", "entity_transaction_details"]) || [];
      const pageSize = Number(params.page_size) || list.length;
      set({
        transactions: append ? [...get().transactions, ...list] : list,
        transactionsStatus: STATUS_DONE,
        transactionsNextOffset: (Number(params.page_start_offset) || 0) + pageSize,
        // an empty page means the list is complete, no more requests
        transactionsHasMore: list.length > 0,
        ...(append
          ? {}
          : {
              transactionsHitCount: toHitCount(
                _get(response, ["data", "response", "transactions_hit_count"])
              ),
            }),
      });
    } catch (error) {
      if (get().transactionsRequestId !== requestId) return;
      set({
        transactions: append ? get().transactions : [],
        transactionsStatus: STATUS_FAIL,
      });
    }
  },

  // One-off lookup of a single transaction by number, sending the same request the list
  // search sends. It leaves the list state alone so a list behind a detail view stays put.
  // Resolves to the exact match, or null when the search finds nothing.
  getTransactionByNumber: async (params) => {
    const url = `${APIendpoints.getVendorTransactions}${buildQuery(params)}`;
    const response = await api.get(API_HOST_WMS, url);
    const list =
      _get(response, ["data", "response", "entity_transaction_details"]) || [];
    return (
      list.find((item) => item.transaction_number === params.transaction_number) ||
      null
    );
  },

  getAccrualDetails: async (params) => {
    const requestId = get().accrualDetailsRequestId + 1;
    set({
      accrualDetails: null,
      accrualDetailsStatus: STATUS_BEGIN,
      accrualDetailsRequestId: requestId,
    });
    const url = `${APIendpoints.getAccrualDetails}${buildQuery(params)}`;
    try {
      const response = await api.get(API_HOST_WMS, url);
      if (get().accrualDetailsRequestId !== requestId) return;
      set({
        accrualDetails: _get(response, ["data", "response"], null),
        accrualDetailsStatus: STATUS_DONE,
      });
    } catch (error) {
      if (get().accrualDetailsRequestId !== requestId) return;
      set({ accrualDetails: null, accrualDetailsStatus: STATUS_FAIL });
    }
  },

  // value can be the new value or an updater, like a useState setter
  setPayoutFilter: (key, value) =>
    set((state) => ({
      payoutFilters: {
        ...state.payoutFilters,
        [key]: typeof value === "function" ? value(state.payoutFilters[key]) : value,
      },
    })),

  resetAccrualDetails: () =>
    set((state) => ({
      accrualDetails: null,
      accrualDetailsStatus: "",
      accrualDetailsRequestId: state.accrualDetailsRequestId + 1,
    })),

  resetAccruals: () =>
    set((state) => ({
      accruals: [],
      accrualsStatus: "",
      accrualsRequestId: state.accrualsRequestId + 1,
      accrualsHasMore: true,
      accrualsHitCount: null,
      accrualsNextOffset: 0,
    })),

  resetTransactions: () =>
    set((state) => ({
      transactions: [],
      transactionsStatus: "",
      transactionsRequestId: state.transactionsRequestId + 1,
      transactionsHasMore: true,
      transactionsHitCount: null,
      transactionsNextOffset: 0,
    })),

  resetPayouts: () =>
    set((state) => ({
      payoutFilters: {
        activeTab: PAYOUTS_TABS.PAYMENTS,
        paymentsFilter: [],
        transactionsFilter: [],
        dueRange: null,
        searchTerm: "",
      },
      accrualDetails: null,
      accrualDetailsStatus: "",
      accrualDetailsRequestId: state.accrualDetailsRequestId + 1,
      accruals: [],
      accrualsStatus: "",
      accrualsRequestId: state.accrualsRequestId + 1,
      accrualsHasMore: true,
      accrualsHitCount: null,
      accrualsNextOffset: 0,
      transactions: [],
      transactionsStatus: "",
      transactionsRequestId: state.transactionsRequestId + 1,
      transactionsHasMore: true,
      transactionsHitCount: null,
      transactionsNextOffset: 0,
    })),
}));

export { makeEncodedQueryParams };
export default usePayoutsStore;
